import { fmtNum } from '../ui/ListFoot';
import { ToTop } from '../ui/ToTop';
import { useCovers } from '../ui/useCovers';
import { Fragment } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { navigate, useRoute } from '../lib/router';
import { BOOKS_CHANGED } from '../lib/changed';
import { recall, useRemembered } from '../lib/remember';
import { useNotice } from '../shell/notice';
import { useMatches } from '../shell/hooks';
import { Popover } from '../ui/Popover';
import { PillMenu, PillPanel, SettingMenu } from '../ui/PillMenu';
import { ListFoot } from '../ui/ListFoot';
import { FilterBar, FilterPanel } from '../shelves/Filters';
import type { FilterState, Group } from '../shelves/filters';
import { BulkMenu } from '../queue/parts';
import { bookLabel, todayIn } from '../book/model';
import {
  EXTRA_COLUMNS,
  PAGE,
  SORT_NAMES,
  START_FORMATS,
  acquiredText,
  cleanFormats,
  digitalOnly as allDigital,
  formatChips,
  formatText,
  gone,
  howName,
  lentNames,
  ownedGroups,
  priceText,
  roomName,
  rowAction,
  shownCopies,
  sortCopies,
  canMarkGone,
  signalChips,
  whereText,
  type Extra,
  type OwnedCopy,
  type OwnedFilters,
  type Format,
  type SortKey,
} from './owned';
import { ChipDivider, ChipRow, FilterChip } from '../ui/bits';
import { BookCell, ListRow, ListTable, type Col } from '../ui/ListTable';
import { loadOwnedFacts, type OwnedFacts } from './ownedLoad';
import { gotBack, placeCopies, setCopiesState } from './actions';
import { IdPaste, LendSheet, PlacePicker } from './copyTools';
import { myBooksTab } from './model';
import './my-books.css';

// My books › Owned copies (Phase 6.4 Part 3; agreed with Kristi Oct 31, laptop mockup in chat).
// Answers "which copies do I have, and where are they?": format filter chips on top (opening on
// Paper; several at once, none on = every format: consistency pass R3, Kristi Nov 2);
// the pinned bar as on the Queue (Filters with rows Where · How · State · IDs, then Sort, Columns ▾,
// Select several and ↑ Top); a list sorted by its column headers (Title · Author · Format · Where ·
// Acquired, plus How · Store · Price · Shelf from Columns ▾; the one ListTable, R6), 20 rows at a
// time; one action per row
// with a ▾ for the rest (Mark lost · Mark donated · Open the book); Where set from a row or for
// several copies at once. Every change has Undo.
// Filters review (Kristi, Nov 2): Not placed yet, Missing an ID and Lent 3+ months are worth a
// look, not needs you: ringed signal chips first (also in their Filters rows), no tab dot and no
// teal edge; an empty value in a row is a dashed pill set in place ("Not placed yet ▾" with rooms
// only, "No Kindle ID ▾" with a paste box).

type Load = { kind: 'loading' } | { kind: 'ready'; facts: OwnedFacts } | { kind: 'error'; message: string };

const num = fmtNum;
const copiesText = (n: number) => (n === 1 ? '1 copy' : `${num(n)} copies`);
const FORMATS_KEY = 'my_books:owned:formats';
const BASE_SORTS: SortKey[] = ['title', 'author', 'format', 'where', 'acquired'];

export function OwnedCopies({ wide, slots }: { wide: boolean; slots: { tools: HTMLElement | null; panel: HTMLElement | null } }) {
  const notice = useNotice();
  const { path } = useRoute();
  const today = todayIn();
  const [state, setState] = useState<Load>({ kind: 'loading' });
  const [chosenFormats, setFormats] = useRemembered<Format[]>(FORMATS_KEY, START_FORMATS);
  const formats = cleanFormats(chosenFormats);
  const [filters, setFilters] = useRemembered<OwnedFilters>('my_books:owned:filters', {});
  const [picks, setExtras] = useRemembered<Extra[]>('my_books:owned:columns', []);
  // Always in the Columns ▾ order, whatever order they were ticked in, so headers and cells match.
  // No Where column, and no Shelf to pick, where every copy is digital (Kristi, Oct 31). A ticked
  // Shelf is kept for when you go back to Paper.
  const digitalOnly = allDigital(formats);
  const columnChoices = EXTRA_COLUMNS.filter((x) => !(digitalOnly && x.key === 'shelf'));
  const extras = columnChoices.map((x) => x.key).filter((k) => picks.includes(k));
  const sorts = [...BASE_SORTS.filter((k) => !(digitalOnly && k === 'where')), ...columnChoices.filter((x) => extras.includes(x.key)).map((x) => x.key as SortKey)];
  // A remembered sort by a column that isn't shown here sorts by Title instead (and comes back with the column).
  const [chosenSort, setSort] = useRemembered<{ key: SortKey; desc: boolean }>('my_books:owned:sort', { key: 'title', desc: false });
  const sort = sorts.includes(chosenSort.key) ? chosenSort : { key: 'title' as SortKey, desc: false };
  const [shown, setShown] = useState(PAGE);
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [placing, setPlacing] = useState<string | 'several' | null>(null);
  const [lending, setLending] = useState<OwnedCopy | null>(null);
  const [menu, setMenu] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Up to 1,000 px wide (an upright iPad), Author and Acquired fold under the title, as on the Queue.
  const fold = useMatches('(max-width: 1000px)');

  const load = () =>
    loadOwnedFacts()
      .then((facts) => setState({ kind: 'ready', facts }))
      .catch((e: unknown) => setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) }));
  useEffect(() => {
    void load();
    const again = () => void load();
    window.addEventListener(BOOKS_CHANGED, again);
    return () => window.removeEventListener(BOOKS_CHANGED, again);
  }, []);


  const copies = state.kind === 'ready' ? state.facts.copies : [];
  const rooms = state.kind === 'ready' ? state.facts.rooms : [];
  // A remembered chip that no longer has copies here is dropped, so it can't hide everything.
  const base = useMemo(() => ownedGroups(copies, formats, rooms, today), [copies, formats.join(), rooms, today]);
  const live: OwnedFilters = useMemo(() => {
    const out: OwnedFilters = {};
    for (const g of base) {
      const on = (filters[g.key]?.on ?? []).filter((id) => g.chips.some((c) => c.id === id));
      if (on.length) out[g.key] = { mode: filters[g.key]?.mode ?? 'show', on };
    }
    return out;
  }, [base, filters]);
  // Each chip counts within what the other rows leave (filters review F10, Kristi Nov 2).
  const groups = useMemo(() => ownedGroups(copies, formats, rooms, today, live), [copies, formats.join(), rooms, today, live]);
  const signals = useMemo(() => signalChips(copies, rooms, today, live), [copies, rooms, today, live]);
  const chipList = useMemo(() => formatChips(copies, rooms, today, live), [copies, rooms, today, live]);
  // The Filters panel's Format row (Kristi, Nov 3): the same switches as the format chips on the
  // page (F4), first in the catalog's order, counting within the panel's other rows (F10).
  const panelGroups = useMemo(
    () => [
      { key: 'format', row: 'Format', label: 'Format', hideable: false, chips: chipList.filter((t) => t.count > 0 || formats.includes(t.key)).map((t) => ({ id: t.key, label: t.name, count: t.count })) },
      ...groups,
    ] as unknown as Group[],
    [chipList, groups, formats.join()],
  );
  const panelState = (formats.length ? { ...live, format: { mode: 'show', on: formats } } : live) as FilterState;
  const panelChange = (s: FilterState) => {
    const { format, ...rest } = s as FilterState & { format?: { on: string[] } };
    setFormats(cleanFormats(format?.on ?? []));
    setFilters(rest as OwnedFilters);
  };
  // A signal chip is its Filters chip (F4): on or off together. Turning one on shows every format, so its copies all show.
  const toggleSignal = (g: (typeof signals)[number]) => {
    const s = live[g.group] ?? { mode: 'show' as const, on: [] };
    const on = g.on ? s.on.filter((x) => x !== g.id) : [...(s.mode === 'hide' ? [] : s.on), g.id];
    setFilters({ ...live, [g.group]: { mode: 'show', on } });
    if (!g.on) setFormats([]);
  };
  const filterOn = Object.keys(live).length > 0;
  const list = useMemo(() => sortCopies(shownCopies(copies, formats, live, rooms, today), sort.key, sort.desc, rooms), [copies, rooms, formats.join(), live, sort]);
  const page = list.slice(0, shown);
  // Every book row shows its cover (check-up, Kristi Nov 2): the copy's own edition's, else the book's.
  const covers = useCovers(page.filter((c) => !c.editionCover).map((c) => ({ id: c.bookId, workId: c.workId })));
  const coverOf = (c: OwnedCopy) => c.editionCover ?? covers.get(c.bookId) ?? null;
  // A new view starts at 20 rows with nothing selected.
  useEffect(() => {
    setShown(PAGE);
    setPicked(new Set());
  }, [formats.join(), JSON.stringify(live), sort.key, sort.desc]);

  const open = (c: OwnedCopy) => navigate(`/book/${bookLabel(c.label)}`, { state: { from: path }, keepScroll: wide });
  const sortBy = (key: SortKey) => setSort(sort.key === key ? { key, desc: !sort.desc } : { key, desc: false });
  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };

  // The pinned bar (as the Queue's): Filters, then Sort, Columns ▾, Select several and ↑ Top.
  const bar = (
    <>
      <FilterBar
        groups={panelGroups}
        state={panelState}
        open={filtersOpen}
        onToggle={() => setFiltersOpen(!filtersOpen)}
        onChange={panelChange}
        showing={filterOn || formats.length ? list.length : null}
        noun={['copy', 'copies']}
      />
      <span class="queue-top-end">
        {/* A setting names itself and its value: "Sort: Title ▾" at every width (consistency pass, R4). */}
        <SettingMenu name="Sort" choices={sorts.map((k) => [k, SORT_NAMES[k]] as [SortKey, string])} value={sort.key} onChange={(k) => setSort({ key: k, desc: false })} />
        {wide ? (
          <span class="pop-host">
            <button type="button" class="btn-secondary" aria-haspopup="menu" aria-expanded={menu === 'columns'} onClick={() => setMenu(menu === 'columns' ? null : 'columns')}>
              Columns ▾
            </button>
            <Popover open={menu === 'columns'} onClose={() => setMenu(null)} label="Columns">
              <div class="menu" role="group" aria-label="More columns">
                {columnChoices.map((x) => (
                  <label key={x.key} class="menu-item oc-col-pick">
                    <input type="checkbox" checked={extras.includes(x.key)} onChange={() => setExtras(picks.includes(x.key) ? picks.filter((e) => e !== x.key) : [...picks, x.key])} />
                    {x.name}
                  </label>
                ))}
              </div>
            </Popover>
          </span>
        ) : null}
        <button
          type="button"
          class={`btn-secondary queue-select${selecting ? ' on' : ''}`}
          aria-pressed={selecting}
          aria-label="Select several"
          onClick={() => {
            setSelecting(!selecting);
            setPicked(new Set());
          }}
        >
          {wide ? (selecting ? '✓ Select several' : 'Select several') : selecting ? '✓ Select' : 'Select'}
        </button>
        <ToTop wide={wide} />
      </span>
    </>
  );
  const panel = filtersOpen ? (
    <FilterPanel
      groups={panelGroups}
      state={panelState}
      onChange={panelChange}
      onClose={() => setFiltersOpen(false)}
      showing={list.length}
      noun={['copy', 'copies']}
    />
  ) : null;

  if (state.kind === 'loading') return <p class="shelves-quiet">Loading your copies…</p>;
  if (state.kind === 'error') return <div class="heads-up">Couldn’t load your copies: {state.message}</div>;

  const showWhere = !digitalOnly;
  const extraWidth = (x: Extra) => (x === 'price' || x === 'how' ? '64px' : 'minmax(70px, 1fr)');
  // The one table (R6): sortable headers but the actions column, which has no header (T3).
  const cols: Col<SortKey>[] = [
    ...(selecting
      ? [
          {
            width: '28px',
            label: (
              <input
                type="checkbox"
                aria-label="Select all shown"
                checked={page.length > 0 && page.every((c) => picked.has(c.id))}
                onChange={(e) => setPicked((e.target as HTMLInputElement).checked ? new Set(page.map((c) => c.id)) : new Set())}
              />
            ),
          },
        ]
      : []),
    { label: 'Title', sort: 'title', book: true, class: 'oc-title', width: fold ? 'minmax(150px, 2.4fr)' : 'minmax(160px, 2.2fr)' },
    ...(fold ? [] : [{ label: 'Author', sort: 'author' as SortKey, class: 'oc-author', width: 'minmax(100px, 1.3fr)' }]),
    { label: 'Format', sort: 'format', class: 'oc-format', width: '86px' },
    ...(showWhere ? [{ label: 'Where', sort: 'where' as SortKey, class: 'oc-where', width: 'minmax(100px, 1.3fr)' }] : []),
    ...(fold ? [] : [{ label: 'Acquired', sort: 'acquired' as SortKey, class: 'oc-acq', num: true, width: '104px' }]),
    ...columnChoices
      .filter((x) => extras.includes(x.key))
      .map((x) => ({ label: x.name, sort: x.key as SortKey, class: `oc-${x.key}`, num: x.key === 'price', width: extraWidth(x.key) })),
    { width: fold ? 'minmax(150px, 1.3fr)' : 'minmax(180px, 1.4fr)', class: 'oc-action' },
  ];

  const extraText = (c: OwnedCopy, x: Extra) => (x === 'how' ? howName(c) : x === 'from' ? (c.gotFrom ?? '') : x === 'store' ? (c.store ?? '') : x === 'price' ? priceText(c) : (c.shelf ?? ''));

  // More ▾ beside a row's action (Kristi, Oct 31; labelled since the consistency pass, R4):
  // Mark lost · Mark donated · Open the book.
  const more = (c: OwnedCopy) => {
    const items: { value: string; label: string; run: () => void }[] = [
      ...(!canMarkGone(c) ? [] : [
        { value: 'lost', label: 'Mark lost', run: () => void setCopiesState(notice, [c], 'lost') },
        { value: 'donated', label: 'Mark donated', run: () => void setCopiesState(notice, [c], 'donated') },
      ]),
      { value: 'open', label: 'Open the book', run: () => open(c) },
    ];
    return (
      <PillMenu
        label="More"
        ariaLabel={`More for ${c.title}`}
        sheetTitle={c.title}
        choices={items.map((it) => ({ value: it.value, label: it.label }))}
        onPick={(v) => items.find((it) => it.value === v)?.run()}
      />
    );
  };

  const action = (c: OwnedCopy) => {
    const a = rowAction(c, !wide, today);
    const main = (() => {
      switch (a.kind) {
        case 'still_have':
          return (
            <>
              <span class="oc-quiet">{c.state === 'lost' ? 'Lost' : 'Donated'}</span>
              <button type="button" class="btn-book" onClick={() => void setCopiesState(notice, [c], 'have_it')}>
                I still have it
              </button>
            </>
          );
        case 'got_back':
          return (
            <>
              <span class="oc-quiet">{a.line}</span>
              <button type="button" class="btn-book" onClick={() => void gotBack(notice, c)}>
                Got it back
              </button>
            </>
          );
        case 'lend':
          return (
            <button type="button" class="btn-book" onClick={() => setLending(c)}>
              Lend it…
            </button>
          );
        case 'open':
          return (
            <a class="btn-book" href={a.url} target="_blank" rel="noopener noreferrer">
              {a.label}
            </a>
          );
        case 'add_id': {
          // Worth a look, set in place (filters review, Kristi Nov 2): a dashed "No Kindle ID ▾" opening a paste box.
          const field = c.format === 'audible' ? 'audible_id' : 'asin';
          return (
            <PillPanel
              empty
              label={a.label}
              ariaLabel={`${a.label} for ${c.title}`}
              sheetTitle={`${field === 'asin' ? 'Kindle' : 'Audible'} ID for ${c.title}`}
            >
              {(close) => <IdPaste copy={{ id: c.id, title: c.title }} field={field} author={c.author} onDone={close} />}
            </PillPanel>
          );
        }
        default:
          return null;
      }
    })();
    return (
      <span class="oc-act">
        {main}
        {more(c)}
      </span>
    );
  };

  const whereButton = (c: OwnedCopy) => {
    const text = whereText(c, rooms, !extras.includes('shelf'));
    return (
      <span class="pop-host oc-where-cell">
        {/* A value in a row: only the value, no fill; empty is dashed "Not placed yet ▾" (filters review, Kristi Nov 2). */}
        <button
          type="button"
          class={`btn-secondary pill-menu oc-where-btn${text ? '' : ' empty'}`}
          aria-haspopup="dialog"
          aria-expanded={placing === c.id}
          aria-label={`Where ${c.title} is: ${text ?? 'not placed yet'}`}
          onClick={() => setPlacing(placing === c.id ? null : c.id)}
        >
          {text ?? 'Not placed yet'} <span aria-hidden="true">▾</span>
        </button>
        <Popover open={placing === c.id} onClose={() => setPlacing(null)} label={`Where ${c.title} is`} sheetTitle={`Where is ${c.title}?`}>
          <PlacePicker
            roomsOnly
            rooms={rooms}
            room={roomName(c, rooms)}
            shelf={c.shelf}
            onCancel={() => setPlacing(null)}
            onSave={(room, shelf) =>
              void placeCopies(notice, [c.id], room, shelf, c.title).then((ok) => {
                if (ok) setPlacing(null);
              })
            }
          />
        </Popover>
      </span>
    );
  };
  // Where on a row: the place for paper, a greyed "Digital" for anything else (Kristi, Oct 31).
  // A lost or donated copy says so instead of asking where it is (Kristi, Oct 31).
  const whereCell = (c: OwnedCopy) =>
    gone(c) ? <span class="oc-digital">{c.state === 'lost' ? 'Lost' : 'Donated'}</span> : c.format === 'paper' ? whereButton(c) : <span class="oc-digital">Digital</span>;

  const pickedList = list.filter((c) => picked.has(c.id));

  return (
    <div class="oc">
      {slots.tools ? createPortal(bar, slots.tools) : null}
      {slots.panel && panel ? createPortal(panel, slots.panel) : null}
      <p class="queue-description">{myBooksTab('owned').description}</p>
      {/* One count line (R7 N7): the grey count, then what the tab's dot means in teal words (Kristi,
          Oct 31: a tooltip doesn't show on a phone). "Showing 20 of 412" is the list foot's. */}
      <p class="mb-needs quiet oc-line">
        <span class="oc-count">{copiesText(list.length)}</span>
      </p>

      {/* Signal chips first, ringed (worth a look), then the format chips (filters review F1, Kristi Nov 2).
          Format chips: several at once, none on = every format, a format with none hidden (R3 P2). */}
      <ChipRow label="Worth a look, and formats">
        {signals.map((g) => (
          <FilterChip key={`${g.group}:${g.id}`} label={g.label} count={g.count} worth={g.tipText} on={g.on} onToggle={() => toggleSignal(g)} />
        ))}
        {signals.some((g) => g.count || g.on) ? <ChipDivider /> : null}
        {chipList.map((t) => (
          <FilterChip
            key={t.key}
            label={t.name}
            count={t.count}
            on={formats.includes(t.key)}
            onToggle={() => {
              // From what's kept, not this render's copy, so two quick taps both count.
              const now = cleanFormats(recall(FORMATS_KEY, START_FORMATS));
              setFormats(now.includes(t.key) ? now.filter((f) => f !== t.key) : [...now, t.key]);
            }}
          />
        ))}
      </ChipRow>

      <ListTable
        label="Your copies"
        cols={cols}
        sort={sort}
        onSort={sortBy}
        narrow={!wide}
        class="oc-table"
        empty={copies.length ? 'Nothing here matches.' : 'No copies here.'}
        onClear={
          copies.length && (filterOn || formats.length)
            ? () => {
                setFilters({});
                setFormats([]);
              }
            : undefined
        }
      >
        {page.map((c) => {
          const pick = selecting ? <input type="checkbox" aria-label={`Select ${c.title}`} checked={picked.has(c.id)} onChange={() => toggle(c.id)} /> : null;
          const format = (
            <>
              {formatText(c)}
              {c.how === 'shared' ? <span class="oc-quiet"> · Shared</span> : null}
            </>
          );
          const line = [
            format,
            c.format === 'paper' && !gone(c) ? whereButton(c) : null,
            acquiredText(c) || null,
          ].filter(Boolean);
          return (
            <ListRow
              key={c.id}
              data-row={c.id}
              quiet={gone(c)}
              cells={[
                ...(selecting ? [pick] : []),
                <BookCell title={c.title} cover={coverOf(c)} author={fold ? [c.author, acquiredText(c)].filter(Boolean).join(' · ') : undefined} onOpen={() => open(c)} />,
                ...(fold ? [] : [c.author]),
                format,
                ...(showWhere ? [whereCell(c)] : []),
                ...(fold ? [] : [acquiredText(c)]),
                ...extras.map((x) => extraText(c, x)),
                action(c),
              ]}
              phone={{
                lead: pick,
                book: <BookCell title={c.title} cover={coverOf(c)} author={c.author} onOpen={() => open(c)} />,
                control: action(c),
                line: (
                  <span class="oc-line">
                    {line.map((part, i) => (
                      <Fragment key={i}>
                        {i ? ' · ' : ''}
                        {part}
                      </Fragment>
                    ))}
                  </span>
                ),
              }}
            />
          );
        })}
      </ListTable>

      <ListFoot
        shown={Math.min(shown, list.length)}
        total={list.length}
        page={PAGE}
        onMore={() => setShown(shown + PAGE)}
        onAll={() => setShown(list.length)}
        onFewer={() => setShown(PAGE)}
      />

      {/* The Queue's select-several bar, pinned at the bottom while selecting (Kristi, Oct 31). */}
      {selecting ? (
        <div class="queue-bulk" role="region" aria-label="Change the selected copies">
          <span class="queue-bulk-count">{pickedList.length ? `${num(pickedList.length)} selected` : 'None selected'}</span>
          {pickedList.length ? (
            <>
              <span class="pop-host">
                <button type="button" class="btn-secondary" aria-haspopup="dialog" aria-expanded={placing === 'several'} onClick={() => setPlacing(placing === 'several' ? null : 'several')}>
                  Put in ▾
                </button>
                <Popover open={placing === 'several'} onClose={() => setPlacing(null)} label="Where these copies are" sheetTitle={`Where are these ${pickedList.length} copies?`}>
                  <PlacePicker
                    rooms={rooms}
                    room={null}
                    shelf={null}
                    several={pickedList.length}
                    onCancel={() => setPlacing(null)}
                    onSave={(room, shelf) =>
                      void placeCopies(notice, pickedList.map((c) => c.id), room, shelf, pickedList[0]?.title ?? '1 copy').then((ok) => {
                        if (ok) {
                          setPlacing(null);
                          setPicked(new Set());
                        }
                      })
                    }
                  />
                </Popover>
              </span>
              {/* Lost and donated are for paper copies only (Kristi, Oct 31); digital copies are skipped. */}
              {pickedList.some(canMarkGone) || pickedList.some(gone) ? (
                <BulkMenu
                  label="Mark as"
                  items={[
                    ...(pickedList.some(canMarkGone) ? [
                      { label: 'Lost', run: () => void setCopiesState(notice, pickedList.filter(canMarkGone), 'lost').then(() => setPicked(new Set())) },
                      { label: 'Donated', run: () => void setCopiesState(notice, pickedList.filter(canMarkGone), 'donated').then(() => setPicked(new Set())) },
                    ] : []),
                    ...(pickedList.some(gone) ? [{ label: 'I still have it', run: () => void setCopiesState(notice, pickedList.filter(gone), 'have_it').then(() => setPicked(new Set())) }] : []),
                  ]}
                />
              ) : null}
            </>
          ) : (
            <span class="queue-bulk-hint">Tick copies, or select every copy shown.</span>
          )}
          <span class="queue-bulk-end">
            {list.length > pickedList.length ? (
              <button type="button" class="btn-link" onClick={() => setPicked(new Set(list.map((c) => c.id)))}>
                Select all {num(list.length)}
              </button>
            ) : null}
            {pickedList.length ? (
              <button type="button" class="btn-link" onClick={() => setPicked(new Set())}>
                Clear
              </button>
            ) : null}
          </span>
        </div>
      ) : null}

      <ToTop wide={wide} float above={selecting} />

      {lending ? (
        <LendSheet copy={lending} sub={['Paper', whereText(lending, rooms)].filter(Boolean).join(' · ')} names={lentNames(copies)} today={today} onClose={() => setLending(null)} />
      ) : null}
    </div>
  );
}
